import React from "react";
import {
  Sparkles,
  FileText,
  Target,
  Link2,
  RefreshCw,
  LogOut,
  Loader2,
  FileQuestion,
  AlertCircle,
} from "lucide-react";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { DocumentPreview } from "./DocumentPreview";

interface RunInfo {
  runId: string;
  runName?: string;
}

interface CellReference {
  cell: string;
  sheet: string;
  value: string | number | null;
}

interface ReferenceViewerProps {
  runInfo: RunInfo;
  selectedCell: CellReference | null;
  sourceData: any;
  loading: boolean;
  loadingData?: boolean;
  dataLoaded?: boolean;
  onRefresh: () => void;
  onSignOut?: () => void;
  userEmail?: string;
}

export function ReferenceViewer({
  runInfo,
  selectedCell,
  sourceData,
  loading,
  loadingData,
  dataLoaded: _dataLoaded,
  onRefresh,
  onSignOut,
  userEmail,
}: ReferenceViewerProps) {
  const hasSource = sourceData && !sourceData.notFound && !sourceData.error;

  return (
    <div className="flex flex-col h-full bg-zinc-50/50 font-sans text-zinc-900 relative">
      {/* Header */}
      <header className="px-5 py-4 bg-white border-b border-zinc-200/80 sticky top-0 z-10 backdrop-blur-sm bg-white/90 supports-[backdrop-filter]:bg-white/60">
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-[15px] font-semibold tracking-tight text-zinc-900">
            Reference Viewers
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={onRefresh}
              disabled={loading || !selectedCell}
              className="p-1.5 rounded-md hover:bg-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Refresh"
            >
              <RefreshCw
                className={`w-4 h-4 text-zinc-500 ${loading ? "animate-spin" : ""}`}
              />
            </button>
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-50/50 border border-emerald-100/50">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
              <span className="text-[10px] font-medium text-emerald-700 uppercase tracking-wider">
                Connected
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[12px] text-zinc-500 leading-relaxed truncate max-w-[200px]">
            {runInfo.runName || `Run: ${runInfo.runId.slice(0, 8)}...`}
          </p>
          <div className="flex items-center gap-3">
            {userEmail && (
              <span className="text-[10px] text-zinc-400 truncate max-w-[100px]">{userEmail}</span>
            )}
            {onSignOut && (
              <button
                onClick={onSignOut}
                className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-red-600 transition-colors"
              >
                <LogOut className="w-3 h-3" />
                Sign out
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Data Loading Overlay */}
      {loadingData && (
        <div className="absolute inset-0 bg-white/90 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-center px-6">
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <div>
              <p className="text-lg font-semibold text-zinc-900">Loading Data</p>
              <p className="text-sm text-zinc-500 mt-1">
                Fetching reconciliation data into Excel...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex flex-col p-5 gap-4">
        {/* Selection Card */}
        <section className="bg-white rounded-xl border border-zinc-200 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] p-4 shrink-0 transition-shadow hover:shadow-md duration-300">
          <div className="flex items-start justify-between mb-4">
            <div>
              <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1 block">
                Selected Cell
              </span>
              <div className="flex items-center gap-2">
                {selectedCell ? (
                  <span className="text-xl font-mono font-medium text-zinc-900 tracking-tight tabular-nums">
                    {selectedCell.sheet}!{selectedCell.cell}
                  </span>
                ) : (
                  <span className="text-lg text-zinc-400 italic">
                    Click a cell to inspect
                  </span>
                )}
              </div>
            </div>
            {hasSource && <ConfidenceBadge level={sourceData.confidence || "medium"} />}
          </div>
          {selectedCell && (
            <div className="flex items-center gap-3 pt-3 border-t border-zinc-100">
              <div className="flex items-center gap-1.5 min-w-0">
                <Target className="w-3.5 h-3.5 text-zinc-400" />
                <span className="text-[11px] text-zinc-600 truncate">
                  Value:{" "}
                  <span className="font-mono text-zinc-900">
                    {selectedCell.value !== null && selectedCell.value !== ""
                      ? String(selectedCell.value)
                      : "(empty)"}
                  </span>
                </span>
              </div>
            </div>
          )}
        </section>

        {/* Loading State */}
        {loading && (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-zinc-400">
              <Loader2 className="w-8 h-8 animate-spin" />
              <span className="text-sm">Finding source reference...</span>
            </div>
          </div>
        )}

        {/* No Cell Selected */}
        {!loading && !selectedCell && (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-zinc-400 text-center px-6">
              <Target className="w-12 h-12 opacity-50" />
              <div>
                <p className="text-sm font-medium text-zinc-600">No cell selected</p>
                <p className="text-xs mt-1">
                  Click on a cell in your spreadsheet to see its source reference
                </p>
              </div>
            </div>
          </div>
        )}

        {/* No Source Found */}
        {!loading && selectedCell && sourceData?.notFound && (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-zinc-400 text-center px-6">
              <FileQuestion className="w-12 h-12 opacity-50" />
              <div>
                <p className="text-sm font-medium text-zinc-600">No source found</p>
                <p className="text-xs mt-1">
                  This cell doesn't have a mapped source reference in the reconciliation
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {!loading && selectedCell && sourceData?.error && (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-red-400 text-center px-6">
              <AlertCircle className="w-12 h-12 opacity-50" />
              <div>
                <p className="text-sm font-medium text-red-600">Failed to load source</p>
                <p className="text-xs mt-1 text-zinc-500">
                  There was an error fetching the source reference. Try refreshing.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Source Data Display */}
        {!loading && hasSource && (
          <>
            {/* Document Preview */}
            <section className="flex-1 min-h-0 flex flex-col">
              <div className="flex items-center justify-between mb-2 px-1">
                <h3 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                  Source Document
                </h3>
                {sourceData.page && (
                  <span className="text-[10px] text-zinc-400 font-mono">
                    Page {sourceData.page}/{sourceData.total_pages || "?"}
                  </span>
                )}
              </div>
              <DocumentPreview
                imageUrl={sourceData.image_url || sourceData.annotated_image}
                fileName={sourceData.document_name || sourceData.filename || "Source Document"}
              />
            </section>

            {/* AI Context Card */}
            <section className="bg-white rounded-xl border border-zinc-200 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] overflow-hidden shrink-0">
              <div className="px-4 py-2.5 border-b border-zinc-100 bg-zinc-50/30 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-zinc-400" />
                  <h3 className="text-[12px] font-semibold text-zinc-900">
                    Source Details
                  </h3>
                </div>
              </div>
              <div className="p-4 space-y-3">
                {sourceData.description && (
                  <p className="text-[12px] text-zinc-600 leading-relaxed">
                    {sourceData.description}
                  </p>
                )}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    {
                      label: "Document",
                      value: sourceData.document_type || sourceData.filename || "PDF",
                      icon: FileText,
                    },
                    {
                      label: "Location",
                      value: sourceData.source_location || sourceData.bbox ? "Found" : "N/A",
                      icon: Target,
                    },
                    {
                      label: "Mapped To",
                      value: selectedCell
                        ? `${selectedCell.sheet}!${selectedCell.cell}`
                        : "N/A",
                      icon: Link2,
                      mono: true,
                    },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="group bg-zinc-50 hover:bg-zinc-100/80 transition-colors rounded-lg p-2.5 border border-zinc-100"
                    >
                      <div className="flex items-center gap-1.5 mb-1 text-zinc-400 group-hover:text-zinc-500 transition-colors">
                        <item.icon className="w-3 h-3" />
                        <span className="text-[9px] font-semibold uppercase tracking-wider">
                          {item.label}
                        </span>
                      </div>
                      <span
                        className={`text-[11px] font-medium text-zinc-900 block truncate ${item.mono ? "font-mono text-blue-600" : ""}`}
                      >
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="px-5 py-3 bg-white border-t border-zinc-200 text-[11px] text-zinc-400 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
          <span>{loading ? "Loading..." : "Ready"}</span>
        </div>
        <span className="font-mono text-[10px] opacity-60">Rima Excel v1.0</span>
      </footer>
    </div>
  );
}
